import { api } from "./api";
import type {
  CompanyType,
  CurrentLicensesSearchResponse,
  LicenseResponse,
  PortOperatorLicenseReceiptPayload,
  PortOperatorLicenseRenewalRequestPayload,
  PortOperatorLicenseRequestPayload,
  ServiceDeskLicensesSearchRequest,
  ServiceDeskLicensesSearchResponse,
  VendorLicenseCompletePayload,
  VendorLicenseRenewalRequestPayload,
  VendorLicenseRequestPayload,
  VendorLicensesSearchRequest,
  VendorLicensesSearchResponse,
} from "./licensing/types";

const SERVICE = "bff-license";
let companyTypesRequest: Promise<CompanyType[]> | undefined;

export const getCompanyTypes = (): Promise<CompanyType[]> => {
  companyTypesRequest ??= api.get<CompanyType[]>(SERVICE, "public/company-types").catch((error) => {
    companyTypesRequest = undefined;
    throw error;
  });

  return companyTypesRequest;
};

export type VendorLicenseRequestCreateResponse = void;
type PortLicenseRequestCreateResponse = void;

export const createVendorLicenseRequest = async (
  payload: VendorLicenseRequestPayload,
  transactionId?: string,
): Promise<VendorLicenseRequestCreateResponse> => {
  return api.post<VendorLicenseRequestCreateResponse>(
    SERVICE,
    "public/vendor-licenses",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const createVendorLicenseRenewalRequest = async ({
  licenseId,
  payload,
  transactionId,
}: {
  licenseId: string;
  payload: VendorLicenseRenewalRequestPayload;
  transactionId?: string;
}): Promise<VendorLicenseRequestCreateResponse> => {
  return api.post<VendorLicenseRequestCreateResponse>(
    SERVICE,
    `vendor-licenses/renew/${licenseId}`,
    { ...payload, id: payload.id ?? licenseId },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const getVendorLicenseById = async (id: string) => {
  return api.get<LicenseResponse>(SERVICE, `vendor-license/${id}`);
};

export const getPortOperatorLicenseById = async (id: string) => {
  return api.get<LicenseResponse>(SERVICE, `port-operator-licenses/${id}`);
};

export const postPortOperatorLicenseReceipt = async ({
  id,
  payload,
  transactionId,
}: {
  id: string;
  payload: PortOperatorLicenseReceiptPayload;
  transactionId?: string;
}) => {
  return api.post<void>(
    SERVICE,
    `port-operator-licenses/${id}/receipt`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const searchVendorLicenses = async ({
  page,
  pageSize,
  body,
}: {
  page: number;
  pageSize: number;
  body: VendorLicensesSearchRequest;
}) => {
  return api.post<VendorLicensesSearchResponse>(
    SERVICE,
    `vendor-licenses/search?pageNumber=${page - 1}&pageSize=${pageSize}`,
    body,
  );
};

export const claimLicenseById = ({ id, transactionId }: { id: string; transactionId?: string }) =>
  api.post(
    SERVICE,
    `vendor-licenses/${id}/claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const unclaimLicenseById = ({ id, transactionId }: { id: string; transactionId?: string }) =>
  api.post(
    SERVICE,
    `vendor-licenses/${id}/unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const completeVendorLicenseById = ({
  id,
  payload,
  transactionId,
}: {
  id: string;
  payload: VendorLicenseCompletePayload;
  transactionId?: string;
}) =>
  api.post(
    SERVICE,
    `vendor-licenses/${id}/complete`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const claimPortOperatorLicenseById = ({
  id,
  transactionId,
}: {
  id: string;
  transactionId?: string;
}) =>
  api.post(
    SERVICE,
    `port-operator-licenses/${id}/claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const unclaimPortOperatorLicenseById = ({
  id,
  transactionId,
}: {
  id: string;
  transactionId?: string;
}) =>
  api.post(
    SERVICE,
    `port-operator-licenses/${id}/unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const completePortOperatorLicenseById = ({
  id,
  payload,
  transactionId,
}: {
  id: string;
  payload: VendorLicenseCompletePayload;
  transactionId?: string;
}) =>
  api.post(
    SERVICE,
    `port-operator-licenses/${id}/complete`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const exportVendorLicenseCsvById = ({
  id,
  transactionId,
}: {
  id: string;
  transactionId?: string;
}) => {
  return (api as any).get(SERVICE, `vendor-licenses/${id}/export-csv`, {
    responseType: "blob",
    ...(transactionId ? { transactionId } : {}),
  });
};

export const getCurrentVendorLicense = async (): Promise<LicenseResponse> => {
  return api.get<LicenseResponse>(SERVICE, "vendor-licenses/current");
};

export const createPortOperatorLicenseRequest = async (
  payload: PortOperatorLicenseRequestPayload,
  transactionId?: string,
): Promise<PortLicenseRequestCreateResponse> => {
  return api.post<PortLicenseRequestCreateResponse>(
    SERVICE,
    "port-operator-licenses",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const createPortOperatorLicenseRenewalRequest = async ({
  licenseId,
  payload,
  transactionId,
}: {
  licenseId: string;
  payload: PortOperatorLicenseRenewalRequestPayload;
  transactionId?: string;
}): Promise<PortLicenseRequestCreateResponse> => {
  return api.post<PortLicenseRequestCreateResponse>(
    SERVICE,
    `port-operator-licenses/renew/${licenseId}`,
    { ...payload, id: payload.id ?? licenseId },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const searchPortOperatorLicenses = async ({
  page,
  pageSize,
  body,
}: {
  page: number;
  pageSize: number;
  body: VendorLicensesSearchRequest;
}) => {
  return api.post<VendorLicensesSearchResponse>(
    SERVICE,
    `port-operator-licenses/search?pageNumber=${page - 1}&pageSize=${pageSize}`,
    body,
  );
};

export const searchCurrentLicenses = async ({
  pageNumber = 0,
  pageSize = 25,
}: {
  pageNumber?: number;
  pageSize?: number;
}): Promise<CurrentLicensesSearchResponse> => {
  return api.get<CurrentLicensesSearchResponse>(
    SERVICE,
    `licenses/current?pageNumber=${pageNumber}&pageSize=${pageSize}`,
  );
};

export const searchServiceDeskLicenses = async ({
  page,
  pageSize,
  body,
}: {
  page: number;
  pageSize: number;
  body: ServiceDeskLicensesSearchRequest;
}) => {
  return api.post<ServiceDeskLicensesSearchResponse>(
    SERVICE,
    `licenses/search?pageNumber=${page - 1}&pageSize=${pageSize}`,
    body,
  );
};
